/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Grid, GridItem, Separator } from 'chakra-v3';
import HelpQrCodeSection from './HelpQrCodeSection';
import HelpInfoSection from './HelpInfoSection';

interface HelpDesktopMenuProps {
  whatsappChatbotUrl?: string;
  whatsappWebUrl?: string;
}

/**
 * HelpDesktopMenu component displays the help page layout for desktop (lg and above) viewports only.
 * It shows a QR code section, a vertical separator, and an info section in a grid layout.
 * This component is only rendered at the lg breakpoint and above.
 *
 * @param whatsappChatbotUrl - The WhatsApp chatbot URL to encode in the QR code
 * @param whatsappWebUrl - The WhatsApp Web URL for the link in the info section
 *
 * @example
 * ```tsx
 * <HelpDesktopMenu
 *   whatsappChatbotUrl="https://wa.me/1234567890"
 *   whatsappWebUrl="https://web.whatsapp.com"
 * />
 * ```
 */
const HelpDesktopMenu: React.FC<HelpDesktopMenuProps> = ({ whatsappChatbotUrl, whatsappWebUrl }) => (
  <Grid
    templateColumns={{ base: '1fr', md: '1fr', lg: 'auto 1px auto' }}
    gap={{ base: 6, md: 8, lg: '110px' }}
    alignItems="center"
    justifyContent="center"
    h="100%"
    w="100%"
    display={{ base: 'none', md: 'none', lg: 'grid' }}
  >
    <GridItem>
      <HelpQrCodeSection qrCodeValue={whatsappChatbotUrl} />
    </GridItem>
    <GridItem>
      <Separator orientation="vertical" h="367px" color="separator.tealGradient1" />
    </GridItem>
    <GridItem>
      <HelpInfoSection whatsappWebUrl={whatsappWebUrl} />
    </GridItem>
  </Grid>
);

export default HelpDesktopMenu;
